import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import Error from "../features/Error.jsx";
import Button from "../components/Button/Button.jsx";

import WrapperContainer from "../components/WrapperContainer/WrapperContainer.jsx";
import { APP_ROUTES } from "../router";

import handleNotify from "../utils/helpers/handleNotify.js";
import { apiFetchStatusProcess, apiCreateProcess } from "../api/methods/process.js";

import useRedirect from "../hooks/useRedirect.js";
import useApiKey from "../hooks/useApiKey.js";

import {
  apiCreateCustomerByPhoneNumber,
  apiCreateCustomerByPortal,
} from "../api/methods/customer.js";
import apiFetchLinkAuth from "../api/methods/apiFetchLinkAuth.js";

import { ROISTAT_VISIT_ID } from "../utils/consts";

const ConfirmPersonalData = () => {
  const history = useNavigate();
  const { changeApiKey } = useApiKey();

  const { mobileConnectionInfo } = useSelector(state => state);
  const { apiKey, street, house, flat, phoneNumber, fullName, refUserId } = mobileConnectionInfo;

  const [hasError, setHasError] = useState(false);
  const redirect = useRedirect();

  const REDIRECT_URL = `${window.location.origin}${APP_ROUTES.MOBILE_CONNECTION.CONFIRM_PERSONAL_DATA_ROUTE}`;

  useEffect(() => {
    if (!phoneNumber) {
      history(APP_ROUTES.MOBILE_CONNECTION.ROOT_ROUTE);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await checkUrlParams();
      } catch (error) {
        handleNotify("error", error);
        setHasError(true);
      }
    })();
  }, [window.location.href]);

  const checkUrlParams = async () => {
    try {
      let urlParams = new URLSearchParams(window.location.search);
      urlParams = Object.fromEntries(urlParams.entries());

      const { key, local_client_id, provider_id, code, state } = urlParams;

      /**
       * После успешной авторизации на портале госуслуг произойдет редирект на REDIRECT_URL,
       * указанный в методе для получения ссылки на сам портал авторизации.
       *
       * Далее, получив необходимые get-параметры, создается новый контрагент и процесс
       */
      if (key && local_client_id && provider_id && code && state) {
        await apiCreateCustomerByPortal(
          key,
          local_client_id,
          provider_id,
          code,
          state,
          REDIRECT_URL
        );

        await createProcess();
        await redirectToNextStep();
      }
    } catch (error) {
      throw error;
    }
  };

  /**
   * Получить/создать процесс
   *
   * @param {?String} key
   */
  const createProcess = async key => {
    const _key = key || apiKey;
    const company = localStorage.getItem("partnerProgram");

    await apiCreateProcess(
      _key,
      street.id,
      house.identifire,
      flat,
      fullName,
      phoneNumber,
      refUserId,
      ROISTAT_VISIT_ID,
      company || "-"
    );
  };

  const getLinkAuth = async () => {
    try {
      const { auth_url, auth_key } = await apiFetchLinkAuth(REDIRECT_URL);
      if (auth_url) {
        changeApiKey(auth_key);
        window.location.href = auth_url;
      }
    } catch (error) {
      handleNotify("error", error);
    }
  };

  const skipAuth = async () => {
    try {
      let response;

      /**
       * В случае если по ранее введенному номеру телефона не удалось найти контрагента,
       * то новый контрагент будет создан по номеру телефона
       */
      if (!apiKey) {
        response = await apiCreateCustomerByPhoneNumber(phoneNumber);
        changeApiKey(response.key);
      }

      await createProcess(response?.key);
      await redirectToNextStep(response?.key);
    } catch {
      setHasError(true);
    }
  };

  const redirectToNextStep = async key => {
    const _key = key || apiKey;

    const processStatus = await apiFetchStatusProcess(_key);
    redirect(processStatus);
  };

  if (hasError) return <Error />;

  return (
    <>
      <WrapperContainer title="sm" gap="16">
        Номер подтвержден
      </WrapperContainer>

      <WrapperContainer text="lg" gap="24">
        Для того, чтобы сформировать договор удаленно, необходимо подтвердить данные через
        Госуcлуги.
      </WrapperContainer>

      <WrapperContainer gap="16">
        <Button isStretch color="outline" size="xxl" icon="right" onClick={getLinkAuth}>
          Войти через Госуслуги
          <img src={require("../assets/img/logo-gosuslugi.svg").default} className="icon" alt="" />
        </Button>
      </WrapperContainer>

      <Button isStretch color="secondary" size="xxl" onClick={skipAuth}>
        У меня нет Госуслуг
      </Button>
    </>
  );
};

export default ConfirmPersonalData;
